import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDialogs } from '@toolpad/core/useDialogs';

import { getPackageOptions, getPackageQueryKey, updatePackageAgreementMutation } from '@drivingdoc/api-client/generated/react-query';
import AgreementForm, { AgreementOutputValues } from 'components/AgreementForm';

import FeedbackDialog from 'components/Dialogs/FeedbackDialog';
import { RichTextEditorProvider } from 'components/Editor/RichTextEditor/RichTextEditorProvider';
import TermsTheme from 'components/Editor/themes/TermsTheme';
import { GetPackageResult } from 'types/packages';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';

interface PackageTermsProps {
    packageId: string;
}

export function PackageTerms({ packageId }: PackageTermsProps) {
    const dialogs = useDialogs();
    const queryClient = useQueryClient();
    const updatePackageTerms = useMutation(updatePackageAgreementMutation());

    // fetch the package
    const pkg = useQuery({
        ...getPackageOptions({
            path: {
                id: packageId,
            },
        }),
        enabled: !!packageId,
    });
    const packageData = pkg.data as GetPackageResult | undefined;

    const onSubmit = async (data: AgreementOutputValues) => {
        try {
            // show confirmation dialog
            const confirmed = await dialogs.confirm(`Update Package Terms of Service?`, {
                okText: 'Update',
                cancelText: 'Cancel',
            });

            // if user cancels, return
            if (!confirmed) {
                return;
            }

            const result = await updatePackageTerms.mutateAsync({
                path: {
                    id: packageId,
                },
                body: {
                    content: data.content as unknown as Record<string, unknown>
                },
            });

            if (result) {
                // invalidate the query key
                queryClient.invalidateQueries({
                    queryKey: getPackageQueryKey({
                        path: {
                            id: packageId,
                        },
                    }),
                });

                // show success message
                dialogs.open(FeedbackDialog, {
                    icon: 'success',
                    title: 'Package Terms Updated',
                });
            }
        } catch (error) {
            dialogs.open(FeedbackDialog, {
                title: 'Error',
                message: getApiErrorMessage(error),
            });
        }
    };

    return (
        <RichTextEditorProvider namespace='package-terms' theme={TermsTheme}>
            <AgreementForm
                initialData={{
                    content: packageData?.agreement?.agreement_versions?.[0].content
                }}
                isLoading={pkg.isLoading}
                onSubmit={onSubmit}
            />
        </RichTextEditorProvider>

    );
}

export default PackageTerms;
